<template>
  <div id="app">
    <router-view />
    <b-toast-orchestrator />
  </div>
</template>

<script>
import { computed, watch } from 'vue'
import { useStore } from 'vuex'
import { useToastController } from 'bootstrap-vue-next'

export default {
  setup() {
    const store = useStore()
    const { show } = useToastController()

    // Computed properties
    const alert = computed(() => store.state.alert)
    const alertState = computed(() => alert.value.counter)

    // Watch for alert changes
    watch(alertState, () => {
      if (alert.value.type) {
        const _variant = alert.value.type.replace('alert-', '')
        if (_variant !== 'none') {
          let _message = alert.value.message || alert.value.status
          if (!_message) {
            _message = 'There was a technical error processing your request. Please, try again later.'
          }

          console.log('Showing toast:', { title: 'RATIFY Orchestrator', body: _message, variant: _variant })

          // Use bootstrap-vue-next toast orchestrator API
          show?.({
            props: {
              title: 'RATIFY Orchestrator',
              body: _message,
              variant: _variant,
              solid: true,
              pos: 'top-end',
              bodyClass: `toast-body-${_variant}`,
              headerClass: `toast-header-${_variant}`,
              toastClass: `toast-${_variant}`
            }
          })
        }
      }
    })

    return {}
  }
}
</script>

<style scoped>
.footer {
  position: fixed;
  bottom: 0;
  right: 0;
  font-size: 0.75rem;
  padding: 0.5rem;
  color: rgba(255, 255, 255, 0.8);
  z-index: 1000;
}
</style>

<style>
/* Workspace Theme Global Styles */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

#app {
  min-height: 100vh;
  transition: background-color 0.3s ease;
}

/* Global scrollbar styles */
::-webkit-scrollbar {
  width: var(--scrollbar-width);
  height: var(--scrollbar-width);
}

::-webkit-scrollbar-track {
  background: var(--color-scrollbar-track);
}

::-webkit-scrollbar-thumb {
  background: var(--color-scrollbar-thumb);
  border-radius: var(--border-radius-small);
}

::-webkit-scrollbar-thumb:hover {
  background: var(--color-scrollbar-thumb-hover);
}

/* Update Bootstrap card styles to match color scheme */
:deep(.grid-item .card),
:deep(.grid-item bcard),
:deep(bcard.project-card) {
  height: 100% !important;
  width: 100% !important;
  border: var(--border-width-card) solid var(--color-border-secondary) !important;
  border-radius: var(--border-radius-standard) !important;
  margin: 0 !important;
  padding: 0 !important;
  box-sizing: border-box !important;
  display: flex !important;
  flex-direction: column !important;
  background-color: var(--color-bg-secondary) !important;
  color: var(--color-text-primary) !important;
  transition: all 0.3s ease !important;
}

:deep(.grid-item .card-body),
:deep(.grid-item bcardbody) {
  flex: 1 !important;
  display: flex !important;
  flex-direction: column !important;
}

:deep(.card-header),
:deep(.card-footer) {
  border-color: var(--color-border-primary);
  background-color: var(--color-bg-primary);
  color: var(--color-text-primary);
}

:deep(.text-muted) {
  color: var(--color-text-secondary) !important;
}

:deep(.project-card .text-muted),
:deep(.project-card .card-text) {
  color: var(--color-text-secondary) !important;
}

/* Toast Notification Styling */

/* Container Base Styles */
.toast {
  border: none !important;
  border-radius: 5px !important;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08) !important;
  min-width: 350px !important;
  overflow: hidden !important;
}

/* Header Section - Typography & Layout */
.toast .toast-header {
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  border-bottom: none !important;
  padding: 16px 18px !important;
  font-size: 15px !important;
  font-weight: 600 !important;
}

/* Body Text Styling */
.toast .toast-body {
  padding: 12px 18px 16px 18px !important;
  font-size: 14px !important;
  font-weight: 400 !important;
  line-height: 1.5 !important;
}

/* Close Button Styling */
.toast .toast-header .btn-close {
  opacity: 0.7 !important;
  transition: opacity 0.2s ease, transform 0.2s ease !important;
}

.toast .toast-header .btn-close:hover {
  opacity: 1 !important;
  transform: scale(1.1) !important;
}

/* Success Variant - Green */
.toast.text-success,
.toast.bg-success,
.toast[class*="success"],
.toast-success {
  overflow: hidden !important;
}

.toast.text-success .toast-header,
.toast.bg-success .toast-header,
.toast[class*="success"] .toast-header,
.toast-success .toast-header,
.toast-header-success {
  background-color: #4ade80 !important; /* Medium green for header */
  color: #15803d !important; /* Dark green for title */
  border-bottom: none !important;
}

.toast.text-success .toast-body,
.toast.bg-success .toast-body,
.toast[class*="success"] .toast-body,
.toast-success .toast-body,
.toast-body-success {
  background-color: #86efac !important; /* Lighter green for body */
  color: #166534 !important; /* Medium green for body text */
}

.toast.text-success .toast-header .btn-close,
.toast.bg-success .toast-header .btn-close,
.toast[class*="success"] .toast-header .btn-close,
.toast-success .toast-header .btn-close {
  filter: brightness(0.4) !important;
}

/* Warning Variant - Amber/Orange */
.toast.text-warning,
.toast.bg-warning,
.toast[class*="warning"],
.toast-warning {
  overflow: hidden !important;
}

.toast.text-warning .toast-header,
.toast.bg-warning .toast-header,
.toast[class*="warning"] .toast-header,
.toast-warning .toast-header,
.toast-header-warning {
  background-color: #fbbf24 !important; /* Medium amber for header */
  color: #92400e !important; /* Dark amber for title */
  border-bottom: none !important;
}

.toast.text-warning .toast-body,
.toast.bg-warning .toast-body,
.toast[class*="warning"] .toast-body,
.toast-warning .toast-body,
.toast-body-warning {
  background-color: #fde68a !important; /* Lighter amber for body */
  color: #92400e !important; /* Dark amber for body text */
}

.toast.text-warning .toast-header .btn-close,
.toast.bg-warning .toast-header .btn-close,
.toast[class*="warning"] .toast-header .btn-close,
.toast-warning .toast-header .btn-close {
  filter: brightness(0.3) !important;
}

/* Danger/Error Variant - Red */
.toast.text-danger,
.toast.bg-danger,
.toast[class*="danger"],
.toast-danger {
  overflow: hidden !important;
}

.toast.text-danger .toast-header,
.toast.bg-danger .toast-header,
.toast[class*="danger"] .toast-header,
.toast-danger .toast-header,
.toast-header-danger {
  background-color: #f87171 !important; /* Medium red for header */
  color: #991b1b !important; /* Dark red for title */
  border-bottom: none !important;
}

.toast.text-danger .toast-body,
.toast.bg-danger .toast-body,
.toast[class*="danger"] .toast-body,
.toast-danger .toast-body,
.toast-body-danger {
  background-color: #fecaca !important; /* Lighter red for body */
  color: #991b1b !important; /* Dark red for body text */
}

.toast.text-danger .toast-header .btn-close,
.toast.bg-danger .toast-header .btn-close,
.toast[class*="danger"] .toast-header .btn-close,
.toast-danger .toast-header .btn-close {
  filter: brightness(0.4) !important;
}

/* Info Variant - Blue (optional, keeping for completeness) */
.toast.text-info,
.toast.bg-info,
.toast[class*="info"],
.toast-info {
  overflow: hidden !important;
}

.toast.text-info .toast-header,
.toast.bg-info .toast-header,
.toast[class*="info"] .toast-header,
.toast-info .toast-header,
.toast-header-info {
  background-color: #60a5fa !important; /* Medium blue for header */
  color: #1e3a8a !important; /* Dark blue for title */
  border-bottom: none !important;
}

.toast.text-info .toast-body,
.toast.bg-info .toast-body,
.toast[class*="info"] .toast-body,
.toast-info .toast-body,
.toast-body-info {
  background-color: #93c5fd !important; /* Lighter blue for body */
  color: #1e3a8a !important; /* Dark blue for body text */
}

.toast.text-info .toast-header .btn-close,
.toast.bg-info .toast-header .btn-close,
.toast[class*="info"] .toast-header .btn-close,
.toast-info .toast-header .btn-close {
  filter: brightness(0.4) !important;
}

/* Ensure toast container sits above all other elements */
.b-toast-orchestrator,
.toast-container {
  z-index: 99999 !important;
  position: fixed !important;
}

/* Animation */
.toast {
  animation: slideInRight 0.3s ease-out !important;
}

@keyframes slideInRight {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}
</style>
